import { SectionHeader } from '@components/SectionHeader/SectionHeader';
import { Ionicons } from '@expo/vector-icons';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { SizableText, useTheme, XStack, YStack } from 'tamagui';

interface FoldableSectionProps {
  title: string;
  titleSlot?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  showSectionIndicator?: boolean;
  sectionIndicatorLabel?: string;
}

export const FoldableSection = ({
  title,
  titleSlot,
  children,
  defaultOpen = true,
  collapsible = true,
  showSectionIndicator = false,
  sectionIndicatorLabel = '',
}: FoldableSectionProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const theme = useTheme();

  const toggle = (): void => {
    if (collapsible) {
      setIsOpen((prev) => !prev);
    }
  };

  const iconName = useMemo(
    () => (isOpen ? 'remove-circle-outline' : 'add-circle-outline'),
    [isOpen],
  );

  return (
    <YStack gap="$3" paddingVertical="$2">
      <YStack gap="$2">
        <SectionHeader
          title={title}
          titleSlot={titleSlot}
          onPress={collapsible ? toggle : undefined}
          rightSlot={
            collapsible ? (
              <Ionicons name={iconName} size={18} color={theme.color.get()} />
            ) : null
          }
        />
        {isOpen && showSectionIndicator && (
          <XStack
            alignSelf="flex-start"
            backgroundColor="$warningBg"
            paddingHorizontal="$2"
            paddingVertical="$1"
          >
            <SizableText size="$3" color="$warningFont">
              {sectionIndicatorLabel}
            </SizableText>
          </XStack>
        )}
      </YStack>
      {isOpen && <YStack gap="$3">{children}</YStack>}
    </YStack>
  );
};
